import type { Node, Edge } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { SIBLING_GAP, DEFAULT_NODE_HEIGHT, type Position } from './nodePositioning'
import { smartPlaceChild } from './smartLayout'

// ============================================================================
// TYPES
// ============================================================================

export interface PositionCalculationDeps {
  getNodes?: () => Node<ReactFlowNodeData>[]
  getEdges?: () => Edge[]
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Calculates position for a single child node relative to parent.
 * Uses the same positioning logic as regular child node creation.
 *
 * @param parentNodeId - ID of the parent node
 * @param deps - Dependencies (getNodes, getEdges)
 * @returns Position for the new child node
 */
export function calculateSingleNodePosition(
  parentNodeId: string,
  deps: PositionCalculationDeps
): Position {
  const currentNodes = deps.getNodes?.() ?? []
  const currentEdges = deps.getEdges?.() ?? []

  const parentNode = currentNodes.find((n) => n.id === parentNodeId)
  if (!parentNode) {
    console.warn(
      `[calculateSingleNodePosition] parent node "${parentNodeId}" NOT FOUND — falling back to (400, 200)`
    )
    return { x: 400, y: 200 }
  }

  return smartPlaceChild(parentNode, currentNodes, currentEdges)
}

/**
 * Calculates positions for multiple AI-generated nodes.
 * Places nodes vertically stacked below existing siblings,
 * using actual node heights for accurate spacing.
 *
 * @param parentNodeId - ID of the parent node
 * @param count - Number of positions to calculate
 * @param newNodeHeight - Expected height of the new nodes being created
 * @param deps - Dependencies (getNodes, getEdges, reactFlowInstance)
 * @returns Array of positions for the new nodes
 */
export function calculateBatchPositions(
  parentNodeId: string,
  count: number,
  newNodeHeight: number = DEFAULT_NODE_HEIGHT,
  deps: PositionCalculationDeps
): Position[] {
  const positions: Position[] = []

  // Get the first position (under existing siblings or at base)
  const firstPosition = calculateSingleNodePosition(parentNodeId, deps)
  positions.push(firstPosition)

  // Stack subsequent nodes below each other
  for (let i = 1; i < count; i++) {
    const prevPosition = positions[i - 1]
    positions.push({
      x: prevPosition.x,
      y: prevPosition.y + newNodeHeight + SIBLING_GAP,
    })
  }

  return positions
}
